"use client"

import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import Image from 'next/image'

import {
  badgeCopyPropType,
  templeKeeperBadgeDefaultProps,
  templeKeeperBadgePropTypes,
} from './temple-keeper-badge.types'

// Single premium badge configuration - all premium users get the same badge
const PREMIUM_BADGE_CONFIG = {
  image: '/images/icons/users-badge/oracle-guardian-badge.png',
}

function getCopy(overrideCopy, fallbackCopy) {
  if (overrideCopy && overrideCopy.title && overrideCopy.description) {
    return overrideCopy
  }

  return fallbackCopy
}

function TempleKeeperBadge({
  tier, // Kept for backward compatibility but not used
  copy,
  showLabel,
  tooltipId,
  className,
  onClick,
  disablePortal = false,
}) {
  const t = useTranslations('oracle.templeBadge')
  const triggerRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [tooltipStyle, setTooltipStyle] = useState({})
  const generatedTooltipId = useId()
  const id = tooltipId || `temple-badge-tooltip-${generatedTooltipId}`

  const fallbackCopy = useMemo(
    () => ({
      title: t('premium.title'),
      description: t('premium.description'),
    }),
    [t],
  )
  const { title, description } = useMemo(
    () => getCopy(copy, fallbackCopy),
    [copy, fallbackCopy],
  )

  useEffect(() => {
    setMounted(true)
  }, [])

  const updateTooltipPosition = useCallback(() => {
    if (!triggerRef.current) {
      return
    }

    const rect = triggerRef.current.getBoundingClientRect()
    const top = rect.bottom + window.scrollY + 12
    const left = rect.left + rect.width / 2 + window.scrollX

    setTooltipStyle({
      position: 'absolute',
      top,
      left,
      transform: 'translateX(-50%)',
      minWidth: rect.width + 40,
    })
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    updateTooltipPosition()

    function handleResize() {
      updateTooltipPosition()
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize, { passive: true })

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize)
    }
  }, [isOpen, updateTooltipPosition])

  const showTooltip = useCallback(() => {
    setIsOpen(true)
  }, [])

  const hideTooltip = useCallback(() => {
    setIsOpen(false)
  }, [])

  const triggerProps = {
    ref: triggerRef,
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
    onKeyDown: (event) => {
      if (event.key === 'Escape') {
        hideTooltip()
      }
    },
    'aria-describedby': isOpen ? id : undefined,
    className: clsx(
      'group relative inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1A132B]/80 px-3 py-2 text-[#FBFBFE]',
      'shadow-[0_0_18px_rgba(164,93,232,0.35)] transition focus:outline-none focus:ring-2 focus:ring-[#D4B6FF]/60 focus:ring-offset-2 focus:ring-offset-[#050316]',
      className,
    ),
  }

  const badgeCore = (
    <div
      className={clsx(
        'relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#1A132B]/80 border border-white/20 shadow-inner shadow-black/30',
      )}
    >
      <Image
        src={PREMIUM_BADGE_CONFIG.image}
        alt={title || 'Premium badge'}
        width={64}
        height={64}
        className="object-contain w-full h-full p-1.5"
        // Removed unoptimized - let Next.js optimize badge images
      />
      <div className="pointer-events-none absolute inset-0 bg-linear-to-br from-white/10 via-transparent to-white/5" />
    </div>
  )

  const label = showLabel ? (
    <div className="flex flex-col text-left">
      <span className="text-xs uppercase tracking-[0.2em] text-[#D4B6FF]">{t('label')}</span>
      <span
        className="font-semibold text-sm leading-relaxed text-[#FBFBFE]"
        data-testid="temple-badge-title"
      >
        {title}
      </span>
    </div>
  ) : null

  const TriggerElement = onClick ? 'button' : 'div'

  const triggerElementProps = onClick
    ? {
        type: 'button',
        onClick,
        ...triggerProps,
      }
    : {
        role: 'figure',
        tabIndex: 0,
        ...triggerProps,
      }

  const tooltipContent = (
    <div
      id={id}
      role="tooltip"
      className="z-60 rounded-2xl border border-[#D4B6FF]/40 bg-[#1A132B]/95 px-4 py-3 text-sm text-[#FBFBFE] shadow-2xl backdrop-blur"
      style={disablePortal ? {} : tooltipStyle}
    >
      <div className="flex items-start gap-3">
        {/* Badge Image in Tooltip */}
        <div className="relative w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
          <Image
            src={PREMIUM_BADGE_CONFIG.image}
            alt={title || 'Premium badge'}
            width={48}
            height={48}
            className="object-contain w-full h-full"
            // Removed unoptimized - let Next.js optimize badge images
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#D4B6FF]">{title}</p>
          <p className="text-xs text-[#FBFBFE]/80">{description}</p>
        </div>
      </div>
    </div>
  )

  const tooltipNode = !isOpen ? null : disablePortal || !mounted || !document.body ? (
    <div className="absolute left-1/2 top-full mt-3 w-max -translate-x-1/2">{tooltipContent}</div>
  ) : (
    createPortal(tooltipContent, document.body)
  )

  return (
    <div className="relative inline-flex" data-testid="temple-badge">
      <TriggerElement {...triggerElementProps}>
        {badgeCore}
        {label}
      </TriggerElement>

      {tooltipNode}
    </div>
  )
}

TempleKeeperBadge.propTypes = {
  ...templeKeeperBadgePropTypes,
  copy: PropTypes.oneOfType([badgeCopyPropType, PropTypes.oneOf([null])]),
  disablePortal: PropTypes.bool,
}

TempleKeeperBadge.defaultProps = {
  ...templeKeeperBadgeDefaultProps,
  disablePortal: false,
}

export default TempleKeeperBadge
